import React from 'react';
import {
  TextInput, Textarea, Button, IconButton, JalaliDatePicker, FormField,
} from '@parsina/framework';
import { Plus, Trash2 } from 'lucide-react';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { VITAL_FIELDS, LAB_FIELDS, emptyLabRow, hasStructured } from '../../lib/history.js';

// One شرح حال entry, being written.
//
// ONE editor for all seven sections. Five of them are prose alone; علائم حیاتی
// and آزمایش‌ها additionally show their structured rows. Seven editors would be
// seven places for the closed-set rules to drift, and the set is closed in a way
// that fails SILENTLY if broken: the BFF's prompt builder emits exactly these
// seven sections in a fixed order, so a section it does not know is quietly
// dropped from the teaching brief — the student's typing never reaches the model,
// and the answer looks merely wrong rather than broken (CORE.md §10, rule 6).
//
// ── The prose is the entry; the structure is an addition ─────────────────
//
// `body` is what a student writes and what carries the teaching value.
// `structured` exists so a vitals row can be read as a row — it is never a
// replacement for the sentence, which is why the textarea is above it and is the
// field the section's empty state asks for.
//
// Controlled and non-submitting: the surface owns the values and renders its own
// commit row, because the desktop commits in a Dialog footer and the phone in a
// BottomSheet's docked footer.

export default function EntryEditor({ values, onChange, disabled = false, error = '' }) {
  const { t } = useTranslation();
  const section = values?.section;

  const setField = (key) => (e) => onChange?.({ [key]: e?.target ? e.target.value : e });

  const setVital = (key) => (e) =>
    onChange?.({ vitals: { ...(values.vitals || {}), [key]: e.target.value } });

  const setLab = (index, key) => (e) => {
    const rows = [...(values.labs || [])];
    rows[index] = { ...rows[index], [key]: e.target.value };
    onChange?.({ labs: rows });
  };

  const addLabRow = () => onChange?.({ labs: [...(values.labs || []), emptyLabRow()] });

  const removeLabRow = (index) => {
    const rows = (values.labs || []).filter((_, i) => i !== index);
    // Always leave one row to type into: an editor with no rows offers nothing
    // but an «افزودن سطر» button, which is a step that should not exist.
    onChange?.({ labs: rows.length ? rows : [emptyLabRow()] });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <TextInput
        label={t('history.entry.label')}
        value={values.label}
        onChange={setField('label')}
        placeholder={t('history.entry.labelPlaceholder')}
        helperText={t('common.labels.optional')}
        disabled={disabled}
        maxLength={120}
        data-testid="entry-label"
      />

      <Textarea
        label={t('history.entry.body')}
        value={values.body}
        onChange={setField('body')}
        placeholder={t('history.entry.bodyPlaceholder')}
        rows={6}
        disabled={disabled}
        error={error}
        maxLength={8000}
        // ≥1rem, or iOS zooms the page when the field takes focus. The kit's
        // 13.5px default is under that, and `inputStyle` is the split the kit
        // provides for reaching the control rather than the field.
        inputStyle={{ fontSize: '1rem' }}
        data-testid="entry-body"
      />

      {/*
        WHEN IT HAPPENED IN THE CASE — not when the row was typed.
        Null is the norm and is a different fact from `createdAtMs`: a student
        writes «سه روز پیش تب کرد» far more often than they date a finding.
      */}
      <FormField label={t('history.entry.recordedAt')} helperText={t('history.entry.recordedAtHelper')}>
        <JalaliDatePicker
          value={values.recordedAt || ''}
          onChange={(next) => onChange?.({ recordedAt: next || '' })}
          disabled={disabled}
        />
      </FormField>

      {hasStructured(section) && (
        <div
          style={{
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            padding: 14,
            background: CT.bg,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: C.textPrimary }}>
              {t('history.structured.title')}
            </div>
            <div style={{ fontSize: 11.5, color: C.textMuted, marginTop: 3 }}>
              {t('history.structured.helper')}
            </div>
          </div>

          {section === 'vitals' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
              {VITAL_FIELDS.map((f) => (
                <TextInput
                  key={f.key}
                  label={f.unitKey ? `${t(f.labelKey)} (${t(f.unitKey)})` : t(f.labelKey)}
                  value={values.vitals?.[f.key] ?? ''}
                  onChange={setVital(f.key)}
                  placeholder={f.placeholder}
                  disabled={disabled}
                  // A reading is a digit run and reads left-to-right in both
                  // layouts. «120/80» typed into an RTL box has its slash
                  // reordered on screen while the value stays correct — so the
                  // student proofreads a string that is not the one being stored.
                  dir="ltr"
                  inputMode={f.numeric ? 'decimal' : 'text'}
                  maxLength={20}
                  data-testid={`vital-${f.key}`}
                />
              ))}
            </div>
          )}

          {section === 'labs' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {(values.labs || []).map((row, index) => (
                // Keyed by INDEX, deliberately: a lab row has no id until it is
                // stored, and keying on `name` would remount the field the moment
                // the student typed the first letter of it, losing focus.
                // eslint-disable-next-line react/no-array-index-key
                <div key={index} style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                  <div
                    style={{
                      flex: 1,
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
                      gap: 8,
                      minWidth: 0,
                    }}
                  >
                    {LAB_FIELDS.map((key) => (
                      <TextInput
                        key={key}
                        label={t(`history.labs.${key}`)}
                        value={row?.[key] ?? ''}
                        onChange={setLab(index, key)}
                        disabled={disabled}
                        // `value` is stored as TEXT, and this is the reason the
                        // whole payload is a JSON string rather than a
                        // google.protobuf.Struct: Struct's numbers are doubles, so
                        // «8.20» comes back 8.2 and «<0.01» cannot be stored at
                        // all. A lab value is a printed reading; nothing in this
                        // app computes with one.
                        dir="ltr"
                        maxLength={60}
                        data-testid={`lab-${index}-${key}`}
                      />
                    ))}
                  </div>
                  <IconButton
                    variant="ghost"
                    size={40}
                    icon={<Trash2 size={16} color={C.red} />}
                    label={t('history.labs.removeRow')}
                    onClick={() => removeLabRow(index)}
                    disabled={disabled}
                  />
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addLabRow} disabled={disabled} data-testid="lab-add-row">
                <Plus size={14} aria-hidden />
                {t('history.labs.addRow')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
